import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { useSnackbar } from '../../../utils/useSnackbar';
import { setActivePage } from '../../../features/navigation/navigationSlice';
import { getPlans, createPlan } from '../../../utils/api/plan_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import PageHeader from '../../Header/PageHeader';
import TableComponent from '../../TableComponent';
import { renderTableCell } from '../../../utils/commonfunctions';
import styles from '../../../styles/component.module.scss';
import adminForm from '../../../styles/admin/adminInlineForms.module.scss';
import pageStyles from '../../../styles/admin/planManagement.module.scss';
import { FormWrapper } from '../../FormComponent';
import ButtonComponent from '../../customButton';

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const CreatePlanForm = ({ onSuccess, disabled, ariaLabelledBy, stacked }) => {
  const { showSnackbar } = useSnackbar();
  const fields = [
    {
      name: 'planName',
      type: 'text',
      label: 'Name',
      required: true,
      disabled,
      className: stacked ? adminForm.planName_stacked : adminForm.planName_row,
    },
    {
      name: 'planCreditLimit',
      type: 'number',
      label: 'Credit limit',
      required: true,
      disabled,
      min: 1,
      step: 1,
      className: stacked ? adminForm.planCredit_stacked : adminForm.planCredit_row,
    },
  ];

  const handleSubmit = async (data, { reset, setError }) => {
    const nameTrim = (data.planName || '').trim();
    const credit = parseInt(data.planCreditLimit, 10);

    if (!nameTrim) {
      setError('planName', { message: 'Name is required' });
      return;
    }
    if (!Number.isInteger(credit) || credit < 1) {
      setError('planCreditLimit', { message: 'Must be at least 1' });
      return;
    }

    try {
      const response = await createPlan({
        name: nameTrim,
        credit_limit: credit,
      });
      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || 'Plan created successfully', 'success');
        reset({ planName: '', planCreditLimit: '' });
        onSuccess();
      } else {
        showSnackbar(response.error || 'Failed to create plan', 'error');
      }
    } catch (err) {
      showSnackbar(err?.message || 'Failed to create plan', 'error');
    }
  };

  const formClass = [adminForm.planForm, stacked && adminForm.planForm_stacked].filter(Boolean).join(' ');

  return (
    <FormWrapper
      fields={fields}
      onSubmit={handleSubmit}
      defaultValues={{ planName: '', planCreditLimit: '' }}
      className={formClass}
      aria-labelledby={ariaLabelledBy}
    >
      {({ isSubmitting }) => (
        <div className={stacked ? adminForm.planSubmit_stacked : adminForm.planSubmit_row}>
          <ButtonComponent
            variant="primary"
            disabled={disabled || isSubmitting}
            loading={isSubmitting}
            ctaText="Create"
            size="medium"
            className="white bg-blue600"
            startIcon={isSubmitting ? null : <AddIcon fontSize="small" />}
          />
        </div>
      )}
    </FormWrapper>
  );
};

const SectionHeader = ({ icon, title, iconClassName, headingId }) => (
  <div className={pageStyles.sectionHeader}>
    <div className={`${pageStyles.iconShell} ${iconClassName}`} aria-hidden>
      {icon}
    </div>
    <h2 className={pageStyles.sectionTitle} id={headingId}>
      {title}
    </h2>
  </div>
);

const PlanManagement = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const showSnakbarRef = React.useRef(showSnackbar);
  showSnakbarRef.current = showSnackbar;
  const activePage = useSelector((state) => state.navigation.activePage);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(setActivePage('Plan Management'));
  }, [dispatch]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlans();
      if (response.status === STATUS.SUCCESS) {
        setPlans(response.data || []);
      } else {
        showSnakbarRef.current(response.error || 'Failed to load plans', 'error');
      }
    } catch (err) {
      showSnakbarRef.current(err?.message || 'Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const breadcrumbItems = [{ label: 'Settings' }, { label: activePage }];

  return (
    <Box className={styles['page-container']} sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          breadcrumbItems={breadcrumbItems}
          title="Plan Management"
          description="View plans and create new plans. Plans define execution, reexecution, and credit limits for organizations."
        />
      </Box>

      <Box
        className={styles['cover-container-col']}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          width: '100%',
          px: 2,
          pt: 2,
          pb: 2,
        }}
      >
        <div className={pageStyles.layout}>
          {/* Left (28%): Create plan */}
          <div className={pageStyles.leftPane}>
            <div className={`${pageStyles.card} ${pageStyles.createCard}`}>
              <SectionHeader
                icon={<PostAddOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Create plan"
                iconClassName={pageStyles.createIcon}
                headingId="create-plan-heading"
              />
              <CreatePlanForm onSuccess={loadPlans} disabled={loading} ariaLabelledBy="create-plan-heading" stacked />
            </div>
          </div>

          {/* Right (70%): Plans table */}
          <div className={pageStyles.rightPane}>
            <div className={`${pageStyles.card} ${pageStyles.plansCard}`}>
              <SectionHeader
                icon={<ViewListOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Plans"
                iconClassName={pageStyles.plansIcon}
              />
              <div className={pageStyles.tableRegion} role="region" aria-label="Plans table">
                <TableComponent
                  title=""
                  isHeaderContentVisible={false}
                  hideColumnsVisibilityButton
                  rows={plans}
                  columns={[
                    { field: 'name', headerName: 'Name', flex: 1, minWidth: 120, renderCell: (params) => renderTableCell(params.value ?? '—') },
                    { field: 'credit_limit', headerName: 'Credit limit', minWidth: 110, renderCell: (params) => renderTableCell(params.value ?? '—') },
                    { field: 'orgs_count', headerName: 'Orgs on plan', minWidth: 110, renderCell: (params) => renderTableCell(params.value ?? 0) },
                    { field: 'created_at', headerName: 'Created at', minWidth: 172, flex: 0, renderCell: (params) => renderTableCell(formatDate(params.value)) },
                  ]}
                  loading={loading}
                  disablePagination
                  disableFooter
                  idField="id"
                  viewType="planManagement"
                  customStyles={{
                    '& .MuiDataGrid-row': { borderBottom: '1px solid', borderColor: 'divider' },
                    '& .MuiDataGrid-cell': { borderBottom: 'none', py: 1 },
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      backgroundColor: 'action.hover',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '0.875rem' },
                  }}
                  customNoData={
                    <div className={pageStyles.noData}>No plans found. Create a plan on the left.</div>
                  }
                  isData={plans.length > 0 || loading}
                  tableHeight="100%"
                />
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default PlanManagement;
